import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useData } from "../context/DataContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import { toast } from "sonner";
import {
  ArrowLeft,
  Calendar,
  User,
  FileText,
  PenTool,
  Check,
  Eye,
  Signature,
  Info,
  Loader2,
} from "lucide-react";
import { Policy, PolicyAssignment, Attachment } from "../types";
import FileAttachments from "../components/FileAttachments";
import { toSydneyDate, formatTimestamp } from "../utils/helpers";

// Helper function to fetch a specific policy assignment
const getPolicyAssignment = async (id: any) => {
    const token = localStorage.getItem("token");
    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || "http://localhost:5001"}/api/policies/${id}/assignment`, {
        method: "GET",
        headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json",
        }
    } );
    const data = await response.json();
    return data.assignment || null;
}

export default function PolicyViewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const {
    markPolicyAsRead,
    signPolicy,
    getUserName,
    getUserById,
    refreshUserAssignments
  } = useData();

  const [policy, setPolicy] = useState<Policy | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [signatureType, setSignatureType] = useState<'Draw' | 'Type'>('Type');
  const [typedSignature, setTypedSignature] = useState('');
  const [showSignatureDialog, setShowSignatureDialog] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const hasRefreshedAssignments = useRef(false);
  const [signature, setSignature] = useState<{ signatureType?: string; signatureData?: string; signedDate?: string; ipAddress?: string; userAgent?: string; } | null>(null);
  const [assignment, setAssignment] = useState<PolicyAssignment | null>(null);
  const [isRead, setIsRead] = useState(false);
  const [isSigned, setIsSigned] = useState(false);
  const [isSigning, setIsSigning] = useState(false);
  const [isMarkingRead, setIsMarkingRead] = useState(false);

  useEffect(() => {
    const fetchPolicy = async (policyId: string) => {
      try {
        const assignmentData = await getPolicyAssignment(policyId);
        if (assignmentData && currentUser) {
          setPolicy(assignmentData.policy);
          setAssignment(assignmentData);
          setSignature({
            signatureType: assignmentData?.signatureType,
            signatureData: assignmentData?.signatureData,
            signedDate: assignmentData?.signedDate,
            ipAddress: assignmentData?.ipAddress,
            userAgent: assignmentData?.userAgent
          });
          setIsRead(!!assignmentData?.readDate);
          setIsSigned(assignmentData.policy.requireSignature && !!assignmentData?.signatureType);
        } else {
          toast.error("Policy not found or access denied.");
          navigate('/my-policies');
        }
      } catch (error) {
        console.error("Failed to fetch policy assignment:", error);
        toast.error("Failed to load the policy.");
        navigate('/my-policies');
      }
    };

    if (id) {
      fetchPolicy(id);
    }
  }, [id, navigate, currentUser]);

  useEffect(() => {
    if (currentUser && !hasRefreshedAssignments.current) {
      refreshUserAssignments();
      hasRefreshedAssignments.current = true;
    }
  }, [currentUser, refreshUserAssignments]);

  // No local formatDate needed, using toSydneyDate and formatTimestamp from helpers

  const getAssignmentStatus = () => {
    if (!policy || !assignment) return 'New';
    if (policy.requireSignature) return isSigned ? 'Signed' : 'Assigned';
    return isRead ? 'Read' : 'Assigned';
  };

  const getStatusBadgeColor = (status: string) => ({
    'Signed': 'bg-green-200 text-green-600',
    'Read': 'bg-blue-200 text-blue-600',
    'Assigned': 'bg-orange-200 text-orange-600',
    'New': 'bg-red-200 text-red-600',
  }[status] || 'bg-gray-200 text-gray-600');

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
    setIsDrawing(true);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    ctx.beginPath();
    ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
    ctx.stroke();
  };

  const stopDrawing = () => setIsDrawing(false);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const handleSignature = async () => {
    if (!policy || !currentUser || isSigning) return;
    setIsSigning(true);
    try {
      let signatureData = '';
      if (signatureType === 'Type') {
        if (!typedSignature.trim()) {
          toast.error('Please enter your name for the typed signature');
          return;
        }
        signatureData = typedSignature.trim();
      } else {
        if (canvasRef.current) {
          signatureData = canvasRef.current.toDataURL();
        }
      }
      await signPolicy(policy.id, { signatureType, signatureData });
      await refreshUserAssignments(); // Refresh data to get the new signature
      // Manually trigger a re-fetch for the current page
      if (id) {
        const assignmentData = await getPolicyAssignment(id);
        if (assignmentData) {
            setPolicy(assignmentData.policy);
            setAssignment(assignmentData);
            setSignature({ signatureType: assignmentData.signatureType, signatureData: assignmentData.signatureData, signedDate: assignmentData.signedDate });
            setIsSigned(true);
        }
      }
      setShowSignatureDialog(false);
      toast.success('Policy signed successfully!');
    } catch (error) {
      toast.error('Failed to sign policy');
    } finally {
      setIsSigning(false);
    }
  };

  const handleMarkAsRead = async () => {
    if (!policy || !currentUser || isMarkingRead) return;
    setIsMarkingRead(true);
    try {
      await markPolicyAsRead(policy.id, currentUser.id);
      await refreshUserAssignments(); // Refresh data to get the new read status
      // Manually trigger a re-fetch for the current page
      if (id) {
        const assignmentData = await getPolicyAssignment(id);
        if (assignmentData) {
            setAssignment(assignmentData);
            setIsRead(true);
        }
      }
      toast.success('Policy marked as read!');
    } catch (error) {
      toast.error('Failed to mark policy as read');
    } finally {
      setIsMarkingRead(false);
    }
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const touch = e.touches[0];
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Calculate scale factors
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    setIsDrawing(true);
    ctx.beginPath();
    ctx.moveTo(
      (touch.clientX - rect.left) * scaleX,
      (touch.clientY - rect.top) * scaleY
    );
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const touch = e.touches[0];
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Calculate scale factors
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    ctx.lineTo(
      (touch.clientX - rect.left) * scaleX,
      (touch.clientY - rect.top) * scaleY
    );
    ctx.stroke();
  };

  if (!policy || !currentUser) {
    return (
      <div className="p-4 md:p-6"><Card><CardContent className="p-6 text-center"><FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" /><h3 className="mb-2">Policy not found</h3><p className="text-muted-foreground">The requested policy could not be found.</p><Button onClick={() => navigate('/my-policies')} className="mt-4">Back to My Policies</Button></CardContent></Card></div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <h1 className="font-bold text-2xl leading-none">{policy.title}</h1>
            <div className="flex items-center gap-2">
              <Badge variant="default">Policy</Badge>
              {isSigned && <Badge className="bg-green-500"><Check className="h-3 w-3 mr-1" />Signed</Badge>}
              {policy.requireSignature && !isSigned && <Badge variant="secondary">Pending Signature</Badge>}
              {isRead && !policy.requireSignature && <Badge className="bg-green-500"><Check className="h-3 w-3 mr-1" />Read</Badge>}
              {!policy.requireSignature && !isRead && <Badge variant="secondary">Unread</Badge>}
            </div>
          </div>
          <p className="text-muted-foreground mt-1 text-sm">Version {policy.version} • Created {formatTimestamp(policy.createdAt)}</p>
        </div>
        <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate(-1)}><ArrowLeft className="h-4 w-4 mr-2" />Back</Button>
        </div>
      </div>

      {policy.status === 'Archived' && (
        <Alert className="border-blue-500 bg-blue-50 text-blue-900">
            <Info className="h-5 w-5" />
            <AlertTitle>Archived</AlertTitle>
            <AlertDescription>This policy is archived and is for reference only. Reason: {policy.archivedReason || 'N/A'}</AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-3 space-y-6">
          <Card>
            <CardHeader><CardTitle>Policy Content / Instructions</CardTitle></CardHeader>
            <CardContent><div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: policy.content.includes('<') ? policy.content : policy.content.replace(/\n/g, '<br />') }} /></CardContent>
          </Card>

          {policy.requireSignature ? (
            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2"><PenTool className="h-5 w-5" />Digital Signature</CardTitle></CardHeader>
              <CardContent>
                {isSigned ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-green-600"><Check className="h-5 w-5" /><span>You have successfully signed this policy.</span></div>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                        <div><strong>Signed by:</strong> {assignment?.user?.name}</div>
                        <div><strong>Date:</strong> {signature && formatTimestamp(signature.signedDate || "")}</div>
                      </div>
                      {signature?.signatureType === 'Draw' && signature.signatureData && <div className="mt-4"><strong>Signature:</strong><div className="mt-2 border rounded p-2 bg-white"><img src={signature.signatureData} alt="Signature" className="max-h-20" /></div></div>}
                      {signature?.signatureType === 'Type' && signature.signatureData && <div className="mt-4"><strong>Signature:</strong><div className="mt-2 border rounded p-2 bg-white font-signature text-2xl">{signature.signatureData}</div></div>}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <p className="text-sm text-muted-foreground">By signing, you acknowledge that you have read, understood, and agree to comply with this policy.</p>
                    <Dialog open={showSignatureDialog} onOpenChange={setShowSignatureDialog}>
                      <DialogTrigger asChild><Button><Signature className="h-4 w-4 mr-2" />Sign Policy</Button></DialogTrigger>
                      <DialogContent className="sm:max-w-md">
                        <DialogHeader><DialogTitle>Sign Policy</DialogTitle></DialogHeader>
                        <div className="space-y-4">
                          <div className="flex gap-2">
                            <Button variant={signatureType === 'Type' ? 'default' : 'outline'} onClick={() => setSignatureType('Type')} className="flex-1">Type</Button>
                            <Button variant={signatureType === 'Draw' ? 'default' : 'outline'} onClick={() => setSignatureType('Draw')} className="flex-1">Draw</Button>
                          </div>
                          {signatureType === 'Type' ? (
                            <div className="space-y-2">
                              <label className="text-sm font-medium">Enter your full name:</label>
                              <Input value={typedSignature} onChange={(e) => setTypedSignature(e.target.value)} placeholder="Your Full Name" className="font-signature text-lg" />
                            </div>
                          ) : (
                            <div className="space-y-2">
                              <label className="text-sm font-medium">Draw your signature:</label>
                              <canvas ref={canvasRef} width={400} height={150} className="w-full border rounded-lg cursor-crosshair bg-gray-50" onMouseDown={startDrawing} onMouseMove={draw} onMouseUp={stopDrawing} onMouseLeave={stopDrawing} onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={stopDrawing} />
                              <Button variant="outline" size="sm" onClick={clearCanvas}>Clear</Button>
                            </div>
                          )}
                          <div className="flex justify-end gap-2 pt-4">
                            <Button variant="outline" onClick={() => setShowSignatureDialog(false)} disabled={isSigning}>Cancel</Button>
                            <Button onClick={handleSignature} disabled={signatureType === 'Type' && !typedSignature.trim() || isSigning}>
                              {isSigning ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                              Confirm Signature
                            </Button>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2"><Eye className="h-5 w-5" />Acknowledgment</CardTitle></CardHeader>
              <CardContent>
                {isRead ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-green-600"><Check className="h-5 w-5" /><span>You have acknowledged this policy.</span></div>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                        <div><strong>Acknowledged by:</strong> {currentUser.name}</div>
                        <div><strong>Date:</strong> {formatTimestamp(assignment?.readDate || "")}</div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <p className="text-sm text-muted-foreground">By clicking below, you acknowledge that you have reviewed and understood this policy.</p>
                    <Button onClick={handleMarkAsRead} disabled={isMarkingRead}>
                      {isMarkingRead ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
                      Mark as Read
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <FileAttachments
            resourceType="policies"
            resourceId={id || ''}
            attachments={(policy.attachments || []) as Attachment[]}
            onAttachmentsChange={() => {}}
            disabled={true}
          />
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader><CardTitle>Policy Information</CardTitle></CardHeader>
            <CardContent className="space-y-4 text-sm">
              <div className="flex justify-between"><span>Effective Date</span><strong>{toSydneyDate(policy.effectiveDate)}</strong></div>
              {policy.expiryDate && <div className="flex justify-between"><span>Expiry Date</span><strong>{toSydneyDate(policy.expiryDate)}</strong></div>}
              <div className="flex justify-between"><span>Version</span><strong>{policy.version}</strong></div>
              <div className="flex justify-between items-center"><span>Your Status</span><Badge variant="secondary" className={getStatusBadgeColor(getAssignmentStatus())}>{getAssignmentStatus()}</Badge></div>
              <div className="flex justify-between"><span>Created By</span><strong>{policy.createdBy.name || "Unknown"}</strong></div>
              <div className="flex justify-between"><span>Last Updated</span><strong>{toSydneyDate(policy.updatedAt)}</strong></div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle>My Activity</CardTitle></CardHeader>
            <CardContent className="space-y-3 text-sm">
              {assignment?.readDate && <div className="flex items-center gap-2"><Eye className="h-4 w-4 text-blue-500" /><span>Read on {toSydneyDate(assignment.readDate)}</span></div>}
              {signature?.signedDate && <div className="flex items-center gap-2"><Check className="h-4 w-4 text-green-500" /><span>Signed on {toSydneyDate(signature.signedDate)}</span></div>}
              {!assignment?.readDate && !signature?.signedDate && <p className="text-muted-foreground">No activity yet.</p>}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );

}
